import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams, useLocation, useParams } from 'react-router-dom';
import { useCachedFetch } from '../../hooks/useCachedFetch';
import UnsubscribeSkeleton from './UnsubscribeSkeleton';
import ErrorPage from '../../pages/404Page';
import Confirm from './States/Confirm';
import Success from './States/Success';
import Resubscribed from './States/Resubscribed';
import StillSubscribed from './States/stillSubscribed';
import { IMAGES_BASE_URL } from '../../config/constants';
import { API_URL } from '../../config/constants';
import './UnsubscribeComponent.scss';


const UnsubscribeComponent = ({ hideHeaderFooterSetter }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { lang } = useParams();
    const currentLang = lang || 'en';
    const [searchParams] = useSearchParams();

    const token = searchParams.get('token');

    const { data: translations, loading: translationsLoading } =
        useCachedFetch('/data/locales/unsubscribe.json');

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [email, setEmail] = useState('');
    const [isChecking, setIsChecking] = useState(true);

    const go = useCallback((path = '', replace = false) => {
        const cleanPath = path ? `/${path}` : '';
        navigate(`/${lang}/unsubscribe${cleanPath}`, { replace });
    }, [navigate, lang]);

    const state = useMemo(() => {
        const path = location.pathname;

        if (path.includes('/resubscribed')) return 'resubscribed';
        if (path.includes('/success')) return 'success';
        if (path.includes('/subscribed')) return 'stillSubscribed';

        return 'confirm';
    }, [location.pathname]);

    const t = useMemo(() => {
        const data = translations?.confirm?.[currentLang] || translations?.confirm?.en;
        if (!data) return null;

        return {
            title: data.title,
            subtitle: data.subtitle,
            buttonConfirm: data.buttonConfirm,
            buttonCancel: data.buttonCancel,
            loading: data.loading,
            error: data.error,
            goHome: data.goHome,
            errors: data.errors || {}
        };
    }, [translations, currentLang]);

    const confirmedT = useMemo(() => {
        return translations?.confirmed?.[currentLang] || translations?.confirmed?.en;
    }, [translations, currentLang]);

    const stillSubscribedT = useMemo(() => {
        return translations?.stillSubscribed?.[currentLang] || translations?.stillSubscribed?.en;
    }, [translations, currentLang]);

    const resubscribedT = useMemo(() => {
        return translations?.resubscribe?.[currentLang] || translations?.resubscribe?.en;
    }, [translations, currentLang]);

    const logoSrc = useMemo(
        () => translations?.logo || '/images/logo.png',
        [translations]
    );

    const handleGoHome = useCallback(() => {
        navigate(`/${lang}`);
    }, [navigate, lang]);

    useEffect(() => {
        if (state !== 'confirm') return;
        if (!token) {
            setError('Invalid link');
            setIsChecking(false);
            return;
        }

        const checkToken = async () => {
            try {
                setIsChecking(true);
                const response = await fetch(`${API_URL}/unsubscribe/info?token=${token}`);

                if (!response.ok) {
                    if (response.status === 404) {
                        setError('Token expired or invalid');
                    } else if (response.status === 410) {
                        const data = await response.json();
                        setError(data.error || 'already_unsubscribed');
                    } else {
                        setError('Failed to verify token');
                    }
                    return;
                }

                const data = await response.json();
                setEmail(data.email);
                setError(null);
            } catch (err) {
                setError('Network error. Please try again.');
            } finally {
                setIsChecking(false);
            }
        };

        checkToken();
    }, [token, state]);

    const handleConfirm = useCallback(async () => {
        if (!token) return;

        setIsLoading(true);
        setError(null);

        try {
            const response = await fetch(`${API_URL}/unsubscribe/confirm`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ token }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Failed to unsubscribe');
            }

            go('success', true);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    }, [token, go]);

    const handleCancel = useCallback(() => {
        go('subscribed', true);
    }, [go]);

    const handleResubscribe = useCallback(async () => {
        if (!email) return;

        setIsLoading(true);
        setError(null);

        try {
            const response = await fetch(`${API_URL}/unsubscribe/resubscribe`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: email,
                    language: currentLang
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Failed to resubscribe');
            }

            go('resubscribed', true);
        } catch (err) {
            console.error('Error resubscribing:', err);
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    }, [email, currentLang, go]);

    if (translationsLoading || isChecking) {
        return <UnsubscribeSkeleton />;
    }

    if (!t) {
        return <ErrorPage hideHeaderFooterSetter={hideHeaderFooterSetter} />;
    }

    return (
        <div className="unsubscribe__wrapper">

            <img
                src={`${IMAGES_BASE_URL}${logoSrc}`}
                alt="Logo"
                className="logo__unsubscribe"
                loading="lazy"
            />

            {state === 'confirm' && (
                <Confirm
                    t={t}
                    email={email}
                    error={error}
                    currentLang={currentLang}
                    isLoading={isLoading}
                    handleConfirm={handleConfirm}
                    handleCancel={handleCancel}
                />
            )}

            {state === 'success' && (
                <Success
                    t={confirmedT}
                    handleResubscribe={handleResubscribe}
                    isResubscribing={isLoading}
                    resubscribeError={error}
                />
            )}

            {state === 'stillSubscribed' && (
                <StillSubscribed
                    t={stillSubscribedT}
                    onGoHome={handleGoHome}
                />
            )}

            {state === 'resubscribed' && (
                <Resubscribed
                    t={resubscribedT}
                    onGoHome={handleGoHome}
                />
            )}

        </div>
    );
};

export default React.memo(UnsubscribeComponent);